import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { HeartHandshake, Loader2, CheckCircle2, AlertCircle, Sparkles, Users, Gift, Share2,
  Cake, Shield, BarChart3, PartyPopper, Film, Image as ImageIcon, Megaphone, Quote, Heart, Lightbulb } from 'lucide-react';

// Egen axios-instans uten auth-headers
const pub = axios.create({ baseURL: process.env.REACT_APP_API_URL || '/api' });

const fmt = (n) =>
  Number(n || 0).toLocaleString('nb-NO', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const tomSkjema = () => ({
  belop: 200,
  metode: 'vipps',
  anonym: false,
  giver_navn: '',
  giver_epost: '',
  giver_telefon: '',
  giver_fnr: '',
  skattefradrag: false,
  melding: '',
  samtykke: false,
  frekvens: 'engangs',
  gave_navn: '',
  gave_melding: '',
});

const STIL = {
  side: { minHeight: '100vh', background: '#f5f7fb', padding: '24px 16px', fontFamily: 'system-ui, sans-serif' },
  wrap: { maxWidth: 720, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 16 },
  kort: { background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' },
  hero: { background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', color: '#fff', borderRadius: 16, padding: 28, position: 'relative', overflow: 'hidden' },
  inp: { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' },
  btn: { padding: '12px 18px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 15 },
  bar: { height: 10, background: 'rgba(255,255,255,0.25)', borderRadius: 5, overflow: 'hidden' },
  barIn: { height: '100%', background: '#fff', borderRadius: 5, transition: 'width .5s ease' },
  belopBtn: { padding: '10px 14px', borderRadius: 10, border: '2px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 15 },
  belopBtnAkt: { padding: '10px 14px', borderRadius: 10, border: '2px solid #6366f1', background: '#eef2ff', cursor: 'pointer', fontWeight: 700, fontSize: 15, color: '#4338ca' },
};

const BELOP_FORSLAG = [100, 200, 500, 1000, 2500];

const Giverside = () => {
  const { slug } = useParams();
  const [qs] = useSearchParams();
  const [data, setData] = useState(null);
  const [feil, setFeil] = useState(null);
  const [skjema, setSkjema] = useState(tomSkjema());
  const [sender, setSender] = useState(false);
  const [ferdig, setFerdig] = useState(null); // {id, vipps?}
  const [tickerIdx, setTickerIdx] = useState(0);
  const [visStickyCta, setVisStickyCta] = useState(false);

  // Sticky CTA: vises etter scroll forbi 600px
  useEffect(() => {
    const onScroll = () => setVisStickyCta(window.scrollY > 600);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Live-ticker rotering hver 5s
  useEffect(() => {
    if (!data?.siste_donasjoner?.length) return;
    const t = setInterval(() => setTickerIdx((i) => (i + 1) % data.siste_donasjoner.length), 5000);
    return () => clearInterval(t);
  }, [data?.siste_donasjoner]);

  // Konfetti på ferdig-skjerm
  useEffect(() => {
    if (!ferdig) return;
    const c = document.createElement('canvas');
    c.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:9999';
    c.width = window.innerWidth; c.height = window.innerHeight;
    document.body.appendChild(c);
    const ctx = c.getContext('2d');
    const farger = ['#6366f1', '#ec4899', '#f59e0b', '#10b981', '#3b82f6'];
    const parts = Array.from({ length: 120 }, () => ({
      x: Math.random() * c.width, y: -20 - Math.random() * 200,
      vx: (Math.random() - 0.5) * 4, vy: 2 + Math.random() * 5,
      r: 4 + Math.random() * 6, f: farger[Math.floor(Math.random() * farger.length)],
      rot: Math.random() * Math.PI, vr: (Math.random() - 0.5) * 0.2,
    }));
    let frame = 0; let raf;
    const draw = () => {
      ctx.clearRect(0, 0, c.width, c.height);
      parts.forEach((p) => {
        p.x += p.vx; p.y += p.vy; p.vy += 0.05; p.rot += p.vr;
        ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.rot);
        ctx.fillStyle = p.f; ctx.fillRect(-p.r / 2, -p.r / 2, p.r, p.r * 0.5);
        ctx.restore();
      });
      frame++;
      if (frame < 250) raf = requestAnimationFrame(draw);
      else c.remove();
    };
    raf = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(raf); c.remove(); };
  }, [ferdig]);

  const last = useCallback(async () => {
    try {
      const r = await pub.get(`/public/kampanje/${slug}`);
      setData(r.data);
    } catch (e) {
      setFeil(e?.response?.data?.error || 'Fant ikke kampanjen.');
    }
  }, [slug]);

  useEffect(() => { last(); }, [last]);

  // Vipps return: ?status=done&id=123
  useEffect(() => {
    const st = qs.get('status');
    const id = qs.get('id');
    if (st === 'done' && id) {
      setFerdig({ id: Number(id), vipps: true });
    }
  }, [qs]);

  const settBelop = (v) => setSkjema((s) => ({ ...s, belop: v }));

  const validerOgBygg = () => {
    const b = Number(skjema.belop || 0);
    if (b < 1) { alert('Beløp må være minst 1 kr.'); return null; }
    if (!skjema.anonym && (skjema.giver_navn || skjema.giver_epost) && !skjema.samtykke) {
      alert('Du må samtykke til lagring av kontaktinformasjon.');
      return null;
    }
    if (skjema.skattefradrag && !skjema.giver_fnr) {
      alert('Fødselsnummer er påkrevd for skattefradrag.');
      return null;
    }
    const teamId = Number(qs.get('team') || 0) || null;
    return {
      belop: b,
      valuta: data?.valuta || 'NOK',
      metode: skjema.metode,
      anonym: !!skjema.anonym,
      giver_navn: skjema.anonym ? '' : skjema.giver_navn,
      giver_epost: skjema.anonym ? '' : skjema.giver_epost,
      giver_telefon: skjema.anonym ? '' : skjema.giver_telefon,
      giver_fnr: skjema.skattefradrag ? skjema.giver_fnr : '',
      skattefradrag: !!skjema.skattefradrag,
      melding: skjema.melding,
      samtykke: !!skjema.samtykke,
      frekvens: skjema.frekvens,
      gave_navn: skjema.gave_navn,
      gave_melding: skjema.gave_melding,
      team_id: teamId,
      utm_kilde: qs.get('utm_kilde') || qs.get('utm_source') || '',
      utm_medium: qs.get('utm_medium') || '',
      utm_kampanje: qs.get('utm_kampanje') || qs.get('utm_campaign') || '',
    };
  };

  const donerVipps = async () => {
    const p = validerOgBygg(); if (!p) return;
    setSender(true);
    try {
      const r = await pub.post(`/public/kampanje/${slug}/vipps-init`, p);
      if (r.data?.url) {
        window.location.href = r.data.url;
      } else {
        alert('Klarte ikke starte Vipps-betaling.');
      }
    } catch (e) {
      alert(e?.response?.data?.error || 'Vipps-betaling feilet.');
    } finally { setSender(false); }
  };

  const donerManuell = async () => {
    const p = validerOgBygg(); if (!p) return;
    setSender(true);
    try {
      const r = await pub.post(`/public/kampanje/${slug}/doner`, p);
      setFerdig({ id: r.data.id, vipps: false });
      setSkjema(tomSkjema());
      last();
    } catch (e) {
      alert(e?.response?.data?.error || 'Donasjon feilet.');
    } finally { setSender(false); }
  };

  if (feil) {
    return (
      <div style={STIL.side}>
        <div style={STIL.wrap}>
          <div style={{ ...STIL.kort, textAlign: 'center' }}>
            <AlertCircle size={48} color="#ef4444" style={{ margin: '0 auto 12px' }} />
            <h2>Kampanjen er ikke tilgjengelig</h2>
            <p style={{ color: '#6b7280' }}>{feil}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={STIL.side}>
        <div style={STIL.wrap}>
          <div style={{ ...STIL.kort, textAlign: 'center' }}>
            <Loader2 size={32} className="spin" />
            <p>Laster …</p>
          </div>
        </div>
      </div>
    );
  }

  if (ferdig) {
    return (
      <div style={STIL.side}>
        <div style={STIL.wrap}>
          <div style={{ ...STIL.kort, textAlign: 'center' }}>
            <CheckCircle2 size={56} color="#10b981" style={{ margin: '0 auto 12px' }} />
            <h2>Tusen takk for støtten!</h2>
            {data?.takke_melding ? (
              <p style={{ color: '#374151', whiteSpace: 'pre-line', lineHeight: 1.5 }}>{data.takke_melding}</p>
            ) : (
              <p style={{ color: '#6b7280' }}>
                Bidraget ditt er registrert
                {ferdig.vipps ? ' via Vipps' : ''}.
              </p>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 16, flexWrap: 'wrap' }}>
              <a
                href={`/api/public/kvittering/${ferdig.id}`}
                target="_blank"
                rel="noreferrer"
                style={{ ...STIL.btn, background: '#6366f1', color: '#fff', textDecoration: 'none', display: 'inline-block' }}
              >
                Se kvittering
              </a>
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.origin + '/gi/' + slug + '/share')}`}
                target="_blank" rel="noreferrer"
                style={{ ...STIL.btn, background: '#1877f2', color: '#fff', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <Share2 size={16} /> Del på Facebook
              </a>
              <button style={{ ...STIL.btn, background: '#e5e7eb', color: '#111' }} onClick={() => setFerdig(null)}>
                Gi mer
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const aktivtMal = data.aktivt_mal || data.mal_belop;
  const prog = aktivtMal > 0 ? Math.min(100, (data.innsamlet / aktivtMal) * 100) : 0;
  const naaddeMal = data.mal_belop > 0 && data.innsamlet >= data.mal_belop;

  return (
    <div style={STIL.side}>
      <div style={STIL.wrap}>
        {/* Hero */}
        <div style={STIL.hero}>
          {data.bilde_url && (
            <img src={data.bilde_url} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.25 }} />
          )}
          <div style={{ position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <HeartHandshake size={28} />
              <span style={{ opacity: 0.9, fontSize: 13 }}>{data.firma_navn || 'Innsamling'}</span>
            </div>
            <h1 style={{ margin: '0 0 8px', fontSize: 28, display: 'flex', alignItems: 'center', gap: 10 }}>
              {data.bursdag_modus && data.bursdag_navn
                ? <><Cake size={26} /> {`${data.bursdag_navn} ønsker seg gaver til ${data.tittel}`}</>
                : data.tittel}
            </h1>
            {data.beskrivelse && <p style={{ opacity: 0.95, lineHeight: 1.5 }}>{data.beskrivelse}</p>}
            {(data.verifisert || data.innsamlingskontrollen || data.transparens_url) && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10, fontSize: 12 }}>
                {data.verifisert && (
                  <span style={{ background: 'rgba(16,185,129,0.25)', padding: '4px 10px', borderRadius: 999 }}>
                    ✓ Verifisert
                  </span>
                )}
                {data.innsamlingskontrollen && (
                  <span style={{ background: 'rgba(255,255,255,0.18)', padding: '4px 10px', borderRadius: 999, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <Shield size={12} /> Innsamlingskontrollen
                  </span>
                )}
                {data.orgnr && (
                  <span style={{ background: 'rgba(255,255,255,0.10)', padding: '4px 10px', borderRadius: 999 }}>
                    Org.nr {data.orgnr}
                  </span>
                )}
                {data.transparens_url && (
                  <a href={data.transparens_url} target="_blank" rel="noreferrer"
                    style={{ color: '#fff', textDecoration: 'underline', padding: '4px 10px', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <BarChart3 size={12} /> Transparens-rapport
                  </a>
                )}
              </div>
            )}
            {data.matching_aktiv && (
              <div style={{ marginTop: 12, padding: '10px 14px', background: 'rgba(255,255,255,0.18)', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Sparkles size={18} />
                <span><b>Hver krone telles dobbelt!</b> {data.matching_sponsor || 'En sponsor'} matcher
                {data.matching_max ? ` opp til ${fmt(data.matching_max)} kr` : ' alle donasjoner'}.</span>
              </div>
            )}
            {aktivtMal > 0 && (
              <div style={{ marginTop: 16 }}>
                <div style={STIL.bar}>
                  <div style={{ ...STIL.barIn, width: `${prog}%` }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 14 }}>
                  <span><b>{fmt(data.innsamlet)} kr</b> samlet inn</span>
                  <span>
                    {naaddeMal && data.stretch_mal > 0 ? 'Stretch-mål' : 'Mål'}: {fmt(aktivtMal)} kr
                  </span>
                </div>
                {naaddeMal && data.stretch_mal > 0 && (
                  <div style={{ marginTop: 6, fontSize: 13, opacity: 0.9, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <PartyPopper size={15} /> Hovedmålet er nådd — vi sikter nå mot stretch-målet!
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Team-liste hvis kampanjen tillater peer-to-peer */}
        {Array.isArray(data.teams) && data.teams.length > 0 && (
          <div style={STIL.kort}>
            <h4 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Users size={18} /> Velg team å støtte
            </h4>
            <div style={{ display: 'grid', gap: 8 }}>
              {data.teams.map((t, i) => (
                <a key={t.id} href={`/gi/${slug}/team/${t.slug}`}
                   style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 12, borderRadius: 10, border: '1px solid #e5e7eb', textDecoration: 'none', color: '#111' }}>
                  <div>
                    <b>{i < 3 ? `${i + 1}. ` : ''}{t.navn}</b>
                    {t.eier_navn && <div style={{ fontSize: 12, color: '#6b7280' }}>av {t.eier_navn}</div>}
                  </div>
                  <div style={{ textAlign: 'right', fontSize: 13 }}>
                    <b>{fmt(t.samlet_belop)} kr</b>
                    {t.mal_belop > 0 && <div style={{ color: '#6b7280' }}>av {fmt(t.mal_belop)} kr</div>}
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Enkel Vipps: del nummer direkte */}
        {data.vipps_nummer && (
          <div style={{ ...STIL.kort, borderTop: '4px solid #ff5b24' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div style={{
                background: '#ff5b24', color: '#fff', fontWeight: 800, fontSize: 22,
                padding: '10px 18px', borderRadius: 12, letterSpacing: 0.5, whiteSpace: 'nowrap',
              }}>
                Vipps {data.vipps_nummer}
              </div>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontWeight: 700, fontSize: 15 }}>Vil du heller vippse direkte?</div>
                <p style={{ margin: '2px 0 0', fontSize: 13, color: '#4b5563' }}>
                  Åpne Vipps, velg «Send» og søk opp nummer <b>{data.vipps_nummer}</b>
                  {data.firma_navn ? <> ({data.firma_navn})</> : null}. Skriv gjerne
                  «{data.tittel}» i meldingsfeltet.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Skjema */}
        <div id="skjema" style={STIL.kort}>
          <h3 style={{ marginTop: 0 }}>Gi en gave</h3>

          {data.snitt_donasjon > 0 && (
            <div style={{ padding: '8px 12px', background: '#f3f4f6', borderRadius: 8, fontSize: 13, marginBottom: 10, color: '#4b5563', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Lightbulb size={15} /> Andre givere gir typisk <b>{fmt(data.snitt_donasjon)} kr</b>
              {data.antall_donasjoner ? ` (${data.antall_donasjoner} donasjoner totalt)` : ''}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            {BELOP_FORSLAG.map((b) => (
              <button key={b} type="button"
                style={Number(skjema.belop) === b ? STIL.belopBtnAkt : STIL.belopBtn}
                onClick={() => settBelop(b)}>
                {b} kr
              </button>
            ))}
          </div>
          <label style={{ display: 'block', marginBottom: 12 }}>
            Eget beløp
            <input type="number" min="1" style={STIL.inp}
              value={skjema.belop} onChange={(e) => settBelop(e.target.value)} />
          </label>

          {/* Frekvens */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
            {[
              { v: 'engangs', t: 'Engangsgave' },
              { v: 'maaned', t: 'Hver måned' },
              { v: 'aar', t: 'Hvert år' },
            ].map((f) => (
              <button key={f.v} type="button"
                style={skjema.frekvens === f.v ? STIL.belopBtnAkt : STIL.belopBtn}
                onClick={() => setSkjema({ ...skjema, frekvens: f.v })}>
                {f.t}
              </button>
            ))}
          </div>
          {skjema.frekvens !== 'engangs' && (
            <p style={{ fontSize: 12, color: '#6b7280', marginTop: -4, marginBottom: 12 }}>
              Vi tar kontakt for å sette opp fast trekk via Vipps-avtale eller AvtaleGiro.
            </p>
          )}

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <input type="checkbox" checked={skjema.anonym}
              onChange={(e) => setSkjema({ ...skjema, anonym: e.target.checked })} />
            Gi anonymt
          </label>

          {!skjema.anonym && (
            <>
              <label style={{ display: 'block', marginBottom: 8 }}>Navn
                <input style={STIL.inp} value={skjema.giver_navn}
                  onChange={(e) => setSkjema({ ...skjema, giver_navn: e.target.value })} />
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                <label>E-post
                  <input type="email" style={STIL.inp} value={skjema.giver_epost}
                    onChange={(e) => setSkjema({ ...skjema, giver_epost: e.target.value })} />
                </label>
                <label>Telefon
                  <input style={STIL.inp} value={skjema.giver_telefon}
                    onChange={(e) => setSkjema({ ...skjema, giver_telefon: e.target.value })} />
                </label>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <input type="checkbox" checked={skjema.skattefradrag}
                  onChange={(e) => setSkjema({ ...skjema, skattefradrag: e.target.checked })} />
                Jeg ønsker skattefradrag (krever fødselsnummer)
              </label>
              {skjema.skattefradrag && (
                <label style={{ display: 'block', marginBottom: 8 }}>Fødselsnummer (11 siffer)
                  <input style={STIL.inp} value={skjema.giver_fnr} maxLength={11}
                    onChange={(e) => setSkjema({ ...skjema, giver_fnr: e.target.value.replace(/\D/g, '') })} />
                </label>
              )}
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 10, marginBottom: 12, fontSize: 13, color: '#6b7280' }}>
                <input type="checkbox" checked={skjema.samtykke}
                  onChange={(e) => setSkjema({ ...skjema, samtykke: e.target.checked })} />
                Jeg samtykker til at {data.firma_navn || 'arrangøren'} lagrer mine kontaktopplysninger for å håndtere donasjonen og sende kvittering (GDPR).
              </label>
            </>
          )}

          <details style={{ marginBottom: 12 }}>
            <summary style={{ cursor: 'pointer', fontSize: 14, color: '#374151', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Gift size={14} /> Gi som gave til noen
            </summary>
            <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
              <label>Mottakerens navn
                <input style={STIL.inp} value={skjema.gave_navn}
                  onChange={(e) => setSkjema({ ...skjema, gave_navn: e.target.value })} />
              </label>
              <label>Personlig hilsen
                <textarea rows="2" style={{ ...STIL.inp, resize: 'vertical' }}
                  value={skjema.gave_melding}
                  onChange={(e) => setSkjema({ ...skjema, gave_melding: e.target.value })} />
              </label>
              {skjema.gave_navn && !skjema.giver_epost && !skjema.anonym && (
                <p style={{ fontSize: 12, color: '#9ca3af', margin: 0 }}>
                  Tips: Oppgi din e-post over – så får du tilsendt et gavekort du kan videresende.
                </p>
              )}
            </div>
          </details>

          <label style={{ display: 'block', marginBottom: 16 }}>Hilsen (valgfri)
            <textarea rows="2" style={{ ...STIL.inp, resize: 'vertical' }}
              value={skjema.melding} onChange={(e) => setSkjema({ ...skjema, melding: e.target.value })} />
          </label>

          <div style={{ display: 'grid', gap: 8 }}>
            <button type="button" disabled={sender}
              style={{ ...STIL.btn, background: '#ff5b24', color: '#fff' }}
              onClick={donerVipps}>
              {sender ? 'Sender …' : `Gi ${fmt(skjema.belop)} kr med Vipps`}
            </button>
            <button type="button" disabled={sender}
              style={{ ...STIL.btn, background: '#e5e7eb', color: '#111' }}
              onClick={donerManuell}>
              Eller registrer bankoverføring/manuell betaling
            </button>
          </div>
          <p style={{ marginTop: 12, fontSize: 12, color: '#9ca3af', textAlign: 'center' }}>
            Personopplysninger behandles iht. GDPR. Betalinger via Vipps håndteres av Vipps MobilePay.
          </p>
        </div>

        {/* Siste donasjoner */}
        {Array.isArray(data.siste_donasjoner) && data.siste_donasjoner.length > 0 && (
          <div style={STIL.kort}>
            <h4 style={{ marginTop: 0 }}>Giverveggen</h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {data.siste_donasjoner.map((d, i) => {
                const navn = d.giver_navn || 'Anonym';
                const init = navn.split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase();
                const farger = ['#6366f1', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6'];
                return (
                  <div key={i} title={`${navn} – ${fmt(d.belop)} kr${d.melding ? ` – «${d.melding}»` : ''}`}
                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, width: 60 }}>
                    <div style={{
                      width: 44, height: 44, borderRadius: '50%',
                      background: farger[i % farger.length], color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 700, fontSize: 14,
                    }}>{init}</div>
                    <div style={{ fontSize: 11, color: '#6b7280', textAlign: 'center' }}>{fmt(d.belop)} kr</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* v35: Video */}
        {data.video_url && (
          <div style={STIL.kort}>
            <h4 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: 8 }}><Film size={18} /> Se historien</h4>
            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, borderRadius: 10, overflow: 'hidden' }}>
              <iframe src={data.video_url} title="Kampanjevideo"
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
                allow="accelerometer; autoplay; encrypted-media; picture-in-picture"
                allowFullScreen />
            </div>
          </div>
        )}

        {/* v35: Galleri */}
        {Array.isArray(data.bilder_extra) && data.bilder_extra.length > 0 && (
          <div style={STIL.kort}>
            <h4 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: 8 }}><ImageIcon size={18} /> Bilder</h4>
            <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 6 }}>
              {data.bilder_extra.map((u, i) => (
                <img key={i} src={u} alt="" style={{
                  height: 160, borderRadius: 10, objectFit: 'cover', flexShrink: 0,
                }} />
              ))}
            </div>
          </div>
        )}

        {/* v35: Oppdateringer */}
        {Array.isArray(data.oppdateringer) && data.oppdateringer.length > 0 && (
          <div style={STIL.kort}>
            <h4 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: 8 }}><Megaphone size={18} /> Oppdateringer</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {data.oppdateringer.map((o) => (
                <div key={o.id} style={{ paddingBottom: 12, borderBottom: '1px solid #f3f4f6' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <b>{o.tittel}</b>
                    <span style={{ fontSize: 11, color: '#9ca3af' }}>{(o.publisert || '').slice(0, 10)}</span>
                  </div>
                  {o.bilde_url && <img src={o.bilde_url} alt="" style={{ maxWidth: '100%', borderRadius: 8, marginTop: 8 }} />}
                  <p style={{ margin: '6px 0 0', color: '#374151', whiteSpace: 'pre-line' }}>{o.tekst}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* v35: Testimonials */}
        {Array.isArray(data.testimonials) && data.testimonials.length > 0 && (
          <div style={STIL.kort}>
            <h4 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: 8 }}><Quote size={18} /> Hva sier de andre?</h4>
            <div style={{ display: 'grid', gap: 10 }}>
              {data.testimonials.map((t) => (
                <div key={t.id} style={{ padding: 12, background: '#f9fafb', borderRadius: 10, borderLeft: '4px solid #6366f1' }}>
                  <p style={{ margin: 0, fontStyle: 'italic', color: '#374151' }}>«{t.sitat}»</p>
                  <div style={{ marginTop: 6, fontSize: 13, color: '#6b7280' }}>
                    — <b>{t.navn}</b>{t.rolle ? `, ${t.rolle}` : ''}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* v35: Partnere */}
        {Array.isArray(data.partnere) && data.partnere.length > 0 && (
          <div style={STIL.kort}>
            <h4 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: 8 }}><HeartHandshake size={18} /> Med støtte fra</h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' }}>
              {data.partnere.map((p) => {
                const inner = p.logo_url
                  ? <img src={p.logo_url} alt={p.navn} style={{ maxHeight: 50, maxWidth: 140, objectFit: 'contain' }} />
                  : <span style={{ fontWeight: 600, color: '#374151' }}>{p.navn}</span>;
                return p.lenke
                  ? <a key={p.id} href={p.lenke} target="_blank" rel="noreferrer" title={p.navn}>{inner}</a>
                  : <span key={p.id} title={p.navn}>{inner}</span>;
              })}
            </div>
          </div>
        )}

        <p style={{ textAlign: 'center', fontSize: 12, color: '#9ca3af', paddingBottom: visStickyCta ? 80 : 0 }}>
          Drives av REGNINO. Spørsmål? Kontakt {data.firma_navn || 'arrangøren'}.
        </p>
      </div>

      {/* v35: Live-ticker (chip nede til venstre) */}
      {Array.isArray(data.siste_donasjoner) && data.siste_donasjoner.length > 0 && (
        <div style={{
          position: 'fixed', left: 12, bottom: visStickyCta ? 76 : 12, zIndex: 50,
          background: '#fff', borderRadius: 999, padding: '8px 14px',
          boxShadow: '0 6px 20px rgba(0,0,0,0.15)', fontSize: 13,
          display: 'flex', alignItems: 'center', gap: 8, maxWidth: 'calc(100vw - 24px)',
        }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#10b981' }} />
          <span>
            <b>{data.siste_donasjoner[tickerIdx]?.giver_navn || 'Anonym'}</b>
            {' ga '}
            <b>{fmt(data.siste_donasjoner[tickerIdx]?.belop || 0)} kr</b>
          </span>
        </div>
      )}

      {/* v35: Sticky CTA */}
      {visStickyCta && !ferdig && (
        <div style={{
          position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 40,
          background: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(8px)',
          borderTop: '1px solid #e5e7eb', padding: 12,
          display: 'flex', justifyContent: 'center',
        }}>
          <button
            style={{ ...STIL.btn, background: '#6366f1', color: '#fff', minWidth: 240, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
            onClick={() => document.getElementById('skjema')?.scrollIntoView({ behavior: 'smooth' })}>
            <Heart size={16} /> Gi nå
          </button>
        </div>
      )}
    </div>
  );
};

export default Giverside;
